"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../utils/cn";

const tableVariants = cva("relative w-full overflow-x-auto");

interface TableProps
  extends React.ComponentProps<"table">, VariantProps<typeof tableVariants> {}

function Table({ className, ...props }: TableProps) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
        className,
      )}
      {...props}
    />
  );
}

const tableHeadVariants = cva(
  "text-foreground h-12 px-4 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
  {
    variants: {
      density: {
        default: "px-4",
        compact: "px-2 h-8 text-xs",
        spacious: "px-6 h-14",
      },
    },
    defaultVariants: {
      density: "default",
    },
  },
);

interface TableHeadProps
  extends React.ComponentProps<"th">, VariantProps<typeof tableHeadVariants> {}

function TableHead({ className, density, ...props }: TableHeadProps) {
  return (
    <th
      data-slot="table-head"
      className={cn(tableHeadVariants({ density, className }))}
      {...props}
    />
  );
}

const tableCellVariants = cva(
  "px-4 py-4 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
  {
    variants: {
      density: {
        default: "px-4 py-4",
        compact: "px-2 py-2 text-xs",
        spacious: "px-6 py-6",
      },
    },
    defaultVariants: {
      density: "default",
    },
  },
);

interface TableCellProps
  extends React.ComponentProps<"td">, VariantProps<typeof tableCellVariants> {}

function TableCell({ className, density, ...props }: TableCellProps) {
  return (
    <td
      data-slot="table-cell"
      className={cn(tableCellVariants({ density, className }))}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  tableVariants,
  tableHeadVariants,
  tableCellVariants,
};
